import { Linkedin } from "lucide-react";
import { TEAM } from "@/lib/data";
import TeamPhoto from "@/components/TeamPhoto";

// Dark-navy "Meet our managed-services leaders" strip. Cards are clickable
// (opens the bio modal handled by the parent Team page).
export default function LeadershipStrip({ onSelect }) {
    const leaders = TEAM.filter((m) => m.slug === "gopichand-a");
    return (
        <section className="relative isolate" data-testid="leadership-strip">
            <div className="absolute inset-0 -z-10" style={{ backgroundColor: "#0F1B2E" }} />
            <div className="container-page py-20 md:py-28">
                <div className="max-w-3xl mb-12">
                    <p className="overline text-brand-amber mb-3">Leadership</p>
                    <h2 className="display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
                        Meet our managed-services leader<span className="brand-dot">.</span>
                    </h2>
                    <p className="mt-6 text-white/70 leading-relaxed">
                        A senior partner who personally oversees every Vittaniti engagement — your
                        single point of contact for strategy, escalation and growth.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-stretch">
                    {leaders.map((l, i) => (
                        <button
                            key={l.slug}
                            type="button"
                            onClick={() => onSelect && onSelect(l)}
                            data-testid={`leader-${l.slug}`}
                            className="relative overflow-hidden rounded-2xl animate-fade-up group text-left md:col-span-7"
                            style={{ animationDelay: `${i * 90}ms`, aspectRatio: "4 / 3" }}
                        >
                            <TeamPhoto
                                member={l}
                                className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(15,27,46,0) 30%, rgba(15,27,46,0.9) 100%)",
                                }}
                            />
                            <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
                                <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                                    {l.name}
                                </h3>
                                <p className="overline text-brand-amber mt-2">{l.role}</p>
                                <span className="mt-5 inline-flex items-center gap-2 text-sm text-white/85">
                                    <Linkedin size={14} className="text-brand-amber" /> View profile
                                </span>
                            </div>
                        </button>
                    ))}

                    {/* Companion narrative panel — keeps the layout balanced and adds context */}
                    <div className="md:col-span-5 flex flex-col justify-center text-white">
                        <p className="overline text-brand-amber mb-3">Partner-led, every time</p>
                        <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight">
                            One senior point of contact — not a rotating cast of juniors.
                        </h3>
                        <p className="mt-5 text-white/75 leading-relaxed">
                            Every engagement is reviewed and signed off at the partner level. You
                            get continuity, accountability and a single line of escalation —
                            backed by a delivery team that scales with you as you grow.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-4">
                            <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                                <p className="font-serif text-3xl text-white">7+</p>
                                <p className="overline text-white/55 mt-2">Years experience</p>
                            </div>
                            <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                                <p className="font-serif text-3xl text-white">Big-4</p>
                                <p className="overline text-white/55 mt-2">PwC · KPMG</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
